"use client";

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

/**
 * Geteiltes Kontaktformular - wird sowohl auf /kontakt als vollstaendige Seite als auch
 * als Pop-up direkt neben der E-Mail-Adresse im Impressum eingebunden (zwei
 * Praesentationen derselben Logik, siehe backlog.md -> Feedback-Runde 19 Punkt 3/24/42).
 */
export function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // Honeypot, siehe app/api/contact/route.ts
  const [status, setStatus] = useState<"idle" | "loading" | "sent" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage(null);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message, website }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        setErrorMessage(data?.error ?? null);
        setStatus("error");
        return;
      }
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }

  if (status === "sent") {
    return (
      <p className="text-sm text-ink-secondary">
        Danke für deine Nachricht! Wir melden uns so schnell wie möglich bei dir zurück.
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="contact-name" className="text-sm font-medium text-ink">
          Name
        </label>
        <Input
          id="contact-name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Dein Name"
          className="bg-card"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="contact-email" className="text-sm font-medium text-ink">
          E-Mail-Adresse
        </label>
        <Input
          id="contact-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="deine@email.de"
          className="bg-card"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="contact-message" className="text-sm font-medium text-ink">
          Nachricht
        </label>
        <Textarea
          id="contact-message"
          required
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Wie können wir helfen?"
          className="bg-card"
        />
      </div>
      {/* Honeypot - fuer sehende Nutzer unsichtbar und ohne Tab-Stop, viele einfache Bots
          fuellen trotzdem jedes Feld eines Formulars aus. */}
      <input
        type="text"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute left-[-9999px] h-0 w-0 opacity-0"
      />
      <Button type="submit" disabled={status === "loading"}>
        {status === "loading" ? "Wird gesendet…" : "Nachricht senden"}
      </Button>
      {status === "error" && (
        <p className="text-xs text-negative">
          {errorMessage ?? "Das hat gerade nicht geklappt. Bitte später erneut versuchen."} Alternativ
          direkt an{" "}
          <a href="mailto:info@erikonomie.de" className="underline">
            info@erikonomie.de
          </a>{" "}
          schreiben.
        </p>
      )}
    </form>
  );
}
